import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, from, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MessengerService } from './messenger.service';

export interface PatientEventSearch
{
  memberId: number;
}

@Injectable({
  providedIn: 'root'
})
export class PatientEventService implements OnDestroy
{
  public events: Observable<any>;
  public eventIcons: Map<string, string> = new Map([
    ['Case', 'fas fa-clock icon-bg-dark'],
    ['Task', 'fas fa-check-circle icon-bg-medium'],
    ['Message', 'fas fa-envelope icon-bg-light'],
    ['Transaction', 'fas fa-usd-circle icon-bg-green']]);

  /**
   * History Types with the corresponding translations.
   */
  public historyTypeTexts: Array<{ type: string; text: string }>;

  private messagingLanguageChangedSubscription: Subscription;
  private sourceEvents: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private eventsData: any[] = [];
  private completed: boolean;
  private firstSearch: boolean;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.events = this.sourceEvents.asObservable();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Reset previous search results.
   */
  public reset(): void
  {
    this.firstSearch = true;
    this.completed = false;
    this.eventsData = [];
    this.sourceEvents.next(this.eventsData);
  }

  /**
   * Fetch Patient Events.
   *
   * @param pageSize Indicates the page size required.
   * @param search Search criteria.
   */
  public searchEvents(pageOffset: number, pageSize: number, search: PatientEventSearch): Observable<any>
  {
    if (this.completed)
    {
      return from([true]);
    }

    const progress = this.http
      .get(`/members/events/${search.memberId}?page=${pageOffset}&size=${pageSize}`)
      .pipe(
        map(response => response),
        tap((value: any) =>
        {
          if (value.data.length === 0 && !this.firstSearch)
          {
            this.completed = true;
          }
          else
          {
            value.data.forEach(e =>
            {
              e.time = new Date(e.time);
              e.icon = this.eventIcons.get(e.type);
              e.title = this.getHistoryTitle(e.type);
            });
            this.eventsData = [...this.eventsData, ...value.data];
            this.sourceEvents.next(this.eventsData);
          }
          this.firstSearch = false;
        })
      );
    return progress;
  }


  /**
   * Get the translated title for the history type.
   *
   * @param historyType History type
   * @returns string Translated history type
   */
  private getHistoryTitle(historyType: string): string
  {
    let title = historyType;
    if (this.historyTypeTexts !== undefined)
    {
      const item = this.historyTypeTexts.find(obj => obj.type === historyType);
      if (item !== undefined)
      {
        title = item.text;
      }
    }
    return title;
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.translate.get([
        'HISTORY-TYPE-CASE-OPENED',
        'HISTORY-TYPE-CASE-CLOSED',
        'HISTORY-TYPE-CASE-MANAGER-CHANGED',
        'HISTORY-TYPE-CASE-PRIORITY-CHANGED',
        'HISTORY-TYPE-TASK-OPENED',
        'HISTORY-TYPE-TASK-COMPLETED',
        'HISTORY-TYPE-TASK-CLOSED',
        'HISTORY-TYPE-MESSAGE-CREATED',
        'HISTORY-TYPE-DOCUMENT-ATTACHED',
        'HISTORY-TYPE-DOCUMENT-DELETED',
        'HISTORY-TYPE-TASK-PRIORITY-CHANGED',
        'HISTORY-TYPE-TASK-ASSIGNED',
        'HISTORY-TYPE-TASK-UNASSIGNED',
        'HISTORY-TYPE-TASK-MODIFIED',
        'HISTORY-TYPE-TASK-RESCHEDULED',
        'HISTORY-TYPE-CASE-STATUS-CHANGED',
        'HISTORY-TYPE-CASE-WORKFLOW-STATUS-CHANGED',
        'HISTORY-TYPE-FOLLOW-UP-TASK-OPENED',
        'HISTORY-TYPE-CASE-SENSITIVITIES-CHANGED',
        'HISTORY-TYPE-CASE-BILLABLE-SERVICE-CREATED'
      ]).subscribe({
        next: (res) =>
        {
          this.historyTypeTexts = [];
          for (const key in res)
          {
            if ({}.hasOwnProperty.call(res, key))
            {
              // Name of translation keys matches the history type after removing prefix and replace - by _
              const historyType = key.replace('HISTORY-TYPE-', '').replace(/-/g, '_');
              this.historyTypeTexts.push({ type: historyType, text: res[key] });
            }
          }
        }
      });
    });
  }
}
